import { useState } from 'react'

import CloseIcon from '@mui/icons-material/Close'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import {
  Button,
  Checkbox,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Stack,
  Typography,
} from '@mui/material'
import { Trans, useTranslation } from 'react-i18next'

import { CollapsibleResults } from 'app/lib/components/CollapsibleResults'
import { ProcessingResults } from 'app/lib/utils/process-directives'

import { MemberActivationDate } from './MemberActivationDate'

interface Props {
  activationDate: Date | null
  onActivationDateChange: (date: Date | null) => void
  sendEmailEnabled: boolean
  processingResults: ProcessingResults
  activationMinDate: Date
  onNext: () => void
  onClose: () => void
  onBack: () => void
}

export const Preview: React.FC<Props> = ({
  activationDate,
  onActivationDateChange,
  sendEmailEnabled,
  processingResults,
  activationMinDate,
  onNext,
  onBack,
  onClose,
}) => {
  const [isValid, setIsValid] = useState(true)
  const [isCheckboxChecked, setIsCheckboxChecked] = useState(false)
  const [showCheckboxError, setShowCheckboxError] = useState(false)

  const { t } = useTranslation()

  const hasDeactivatedRecords = (processingResults.deactivatedRecords.length ?? 0) > 0

  const handleContinueClick = () => {
    if (hasDeactivatedRecords && !isCheckboxChecked) {
      setShowCheckboxError(true)
      return
    }
    onNext()
  }

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsCheckboxChecked(event.target.checked)
    if (event.target.checked) {
      setShowCheckboxError(false)
    }
  }

  return (
    <>
      <DialogTitle>
        {t('csvImport.preview.title')}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <CollapsibleResults processingResults={processingResults} />
        <Stack mt={2}>
          <Typography variant="caption1" color="dimgrey">
            {t('csvImport.preview.activationDate.label')}
          </Typography>
          <Typography variant="caption2" color="dimgrey">
            {t('csvImport.preview.activationDate.description')}
          </Typography>
          <MemberActivationDate
            activationDate={activationDate}
            onActivationDateChange={onActivationDateChange}
            activationMinDate={activationMinDate}
            onValidate={setIsValid}
          />
          <Typography variant="caption2" color="dimgrey">
            {sendEmailEnabled
              ? t('csvImport.preview.sendNewMembersAnInviteAllowed')
              : t('csvImport.preview.sendNewMembersAnInviteNotAllowed')}
          </Typography>
          {hasDeactivatedRecords && (
            <Stack direction="column" mt={3}>
              {showCheckboxError && (
                <Stack direction="row" alignItems="center" gap={0.5}>
                  <ErrorOutlineIcon color="error" />

                  <Typography variant="caption1" color="error">
                    {t('csvImport.preview.checkboxRequiredFieldsError', {
                      count: processingResults.deactivatedRecords.length,
                      date: activationDate,
                    })}
                  </Typography>
                </Stack>
              )}
              <FormControlLabel
                label={
                  <Trans
                    i18nKey="csvImport.preview.deactivateMembersCheckbox"
                    values={{
                      count: processingResults.deactivatedRecords.length,
                      date: activationDate ? activationDate.toLocaleDateString() : '',
                    }}
                    components={[<strong></strong>]}
                  />
                }
                control={
                  <Checkbox
                    size="small"
                    sx={{ marginLeft: 1 }}
                    checked={isCheckboxChecked}
                    onChange={handleCheckboxChange}
                    data-testid="deactivate-members-checkbox"
                  />
                }
              />
            </Stack>
          )}
        </Stack>
        <DialogActions>
          <Button onClick={onBack}>{t('global.dialog.back')}</Button>
          <Button
            onClick={handleContinueClick}
            variant="contained"
            data-testid="continue-button"
            disabled={!isValid}
          >
            {t('global.dialog.continue')}
          </Button>
        </DialogActions>
      </DialogContent>
    </>
  )
}
